/**
 * useAgentBadge — Sidebar badge count for Agent Inbox.
 * Polls /api/agent/badge every 30s.
 *
 * Returns:
 * - count: action-required items (needs_review + waiting + missing)
 * - recentAutoApproved: auto-approved today (informational)
 * - color: amber | blue | teal | none
 */
import useSWR from 'swr';
import { useAuthStore } from './useAuth';
import { isOperationsEntitledUser } from './useWarehouseEntitlement';

export interface AgentBadgeData {
    count: number;
    needsReview: number;
    waitingOnCarrier: number;
    missingInformation: number;
    recentAutoApproved: number;
    color: 'amber' | 'blue' | 'teal' | 'none';
}

const EMPTY: AgentBadgeData = {
    count: 0,
    needsReview: 0,
    waitingOnCarrier: 0,
    missingInformation: 0,
    recentAutoApproved: 0,
    color: 'none',
};

const fetcher = (url: string) => {
    const token = useAuthStore.getState().token;
    return fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    }).then(async (res) => {
        if (!res.ok) return EMPTY;
        const json = await res.json();
        // Normalize: backend now returns autoApprovedToday, map to recentAutoApproved
        return {
            ...json,
            recentAutoApproved: json.autoApprovedToday ?? json.recentAutoApproved ?? 0,
        };
    });
};

export function useAgentBadge(): AgentBadgeData {
    const { token, user } = useAuthStore();
    const isWarehouseUser = user?.role === 'warehouse_manager' || user?.role === 'warehouse_staff' || user?.role === 'admin';
    const isOperationsEntitled = isOperationsEntitledUser(user);

    const { data } = useSWR<AgentBadgeData>(
        token && isWarehouseUser && isOperationsEntitled ? '/api/agent/badge' : null,
        fetcher,
        { refreshInterval: 30000, dedupingInterval: 15000 }
    );

    if (!isOperationsEntitled) return EMPTY;

    return data || EMPTY;
}
